import React, { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/Navigation";
import { DietSelectionStep } from "@/components/diet/DietSelectionStep";
import { BasicInfoStep } from "@/components/diet/BasicInfoStep";
import { DietInfoStep } from "@/components/diet/DietIInfoStep";
import { DietPreferencesStep } from "@/components/diet/DietPreferencesStep";
import { DietViewStep } from "@/components/diet/DietViewStep";
import { diets } from "@/i18n/locales/diets";
import { useDietContext } from "@/contexts/DietContext";
import { useAuth } from "@/contexts/AuthContext";
import { DietProvider } from "@/contexts/DietProvider";
import { useToast } from "@/hooks/use-toast";

const BuildDietContent = () => {
    const { t, i18n } = useTranslation();
    const { toast } = useToast();
    const { currentDietId } = useAuth();
    const { setFormData, step, emptyFormData } = useDietContext();
    const [searchParams] = useSearchParams();

    // Controlla se `?update=true`
    const isUpdate = searchParams.get("update") === "true";

    // Query per ottenere la dieta attiva
    const {
        data: activeDiet,
        error,
        isError,
        isLoading,
    } = useQuery({
        queryKey: ["diet", "active"], // Query key
        queryFn: async () => {
            const response = await fetch("/api/diet");
            if (!response.ok) {
                throw new Error("Failed to fetch the active diet");
            }
            return response.json();
        },
        enabled: isUpdate, // Esegui la query solo se `isUpdate` è `true`
        retry: 1, // Ritenta una sola volta in caso di errore
    });

    // Gestione errori
    useEffect(() => {
        if (isError) {
            toast({
                title: t("buildDiet.toasts.loading.title"),
                description: t("buildDiet.toasts.loading.error"),
                variant: "destructive",
            });
            // Inizializza il form a vuoto in caso di errore
            setFormData(() => ({
                ...emptyFormData(),
                ...(isUpdate ? { updateId: currentDietId } : {}),
            }));
        }
    }, [isError, t, toast, setFormData, isUpdate, currentDietId, emptyFormData]);

    // Inizializza il form con i dati della dieta attiva
    useEffect(() => {
        if (activeDiet) {
            setFormData((prev) => ({
                ...prev,
                ...activeDiet,
            }));
        }
    }, [activeDiet, setFormData]);

    const availableDiets = diets[i18n.language] || diets["en"];

    const steps = [
        // Step 1: Selezione della dieta
        () => <DietSelectionStep availableDiets={availableDiets} />,
        // Step 2: Informazioni personali
        () => <BasicInfoStep />,
        // Step 3: Informazioni sulla dieta
        () => <DietInfoStep />,
        // Step 4: Risultati della dieta
        () => <DietPreferencesStep />,
        // Step 5: Visualizzazione della dieta
        () => <DietViewStep />,
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="pt-24 pb-6 px-4 sm:px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">{isLoading ? <p>{t("buildDiet.loading")}</p> : steps[step - 1]()}</div>
            </div>
        </div>
    );
};

const BuildDiet = () => (
    <DietProvider>
        <BuildDietContent />
    </DietProvider>
);

export default BuildDiet;
